import { canonicalizePayload } from "./server-keys";
import { signData } from "./keys";

/**
 * Request a single-use nonce for the PGP Attestation Protocol.
 */
export async function requestSigningNonce(): Promise<{
  nonce: string;
  expiresIn: number;
}> {
  const res = await fetch("/api/signing-nonce", { method: "POST" });
  if (!res.ok) throw new Error("Failed to fetch signing nonce");
  return await res.json();
}

/**
 * Execute the PGP Attestation protocol.
 *
 * 1. Constructs the canonical intent payload.
 * 2. Asks the client's PGP key to sign the exact payload string.
 * 3. Sends the payload + signature to the server to be counter-signed.
 */
export async function requestServerAttestation(
  docHash: string,
  signerEmail: string,
  signerKeyId: string,
  nonce: string,
  fields: Record<string, any>,
  privateKeyArmored: string,
  publicKeyArmored: string,
  passphrase?: string,
): Promise<{ serverMetadata: any; pgpSignature: string }> {
  const timestamp = new Date().toISOString();

  // 1. Construct intent payload exactly as the server will see it
  const payload = {
    docHash,
    signerEmail,
    signerKeyId,
    nonce,
    timestamp,
    fields,
  };

  // The canonical string MUST match what the server verifies
  const payloadString = canonicalizePayload(payload);

  // 2. Client signs it locally
  const pgpSignature = await signData(
    payloadString,
    privateKeyArmored,
    passphrase,
  );

  // 3. Send to server for witnessing
  const attResp = await fetch("/api/attest-signature", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      payloadString,
      payload,
      pgpSignature,
      publicKey: publicKeyArmored,
    }),
  });

  if (!attResp.ok) {
    const errJson = await attResp.json().catch(() => ({}));
    throw new Error(errJson.error || "Server attestation failed");
  }

  const data = await attResp.json();
  return { serverMetadata: data.serverMetadata, pgpSignature };
}
